import { VirtualClinicAnalysisFilterIges } from './iges';

import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

export const BD_IGES_CONDITION_ID = 'iges';
export const FilterConditionIgesPluginType: FilterConditionPluginSpecification<
  VirtualClinicAnalysisFilterIges,
  FilterConditionIgesDto,
  BdIgesCondition
> = {
  type: 'iges',
  category: 'case',
  displayName: { de: 'IGES Kriterien' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionIgesDto,
  ): FilterConditionPluginImpl<VirtualClinicAnalysisFilterIges> {
    return new FilterConditionIgesPlugin(
        VirtualClinicAnalysisFilterIges.fromObject(obj.iges),
      );
  },
  fromCondition(
    jsonObj: BdIgesCondition,
  ): FilterConditionPluginImpl<VirtualClinicAnalysisFilterIges> {
    return new FilterConditionIgesPlugin(
        VirtualClinicAnalysisFilterIges.fromObject(jsonObj),
      );
  },
  newInstance(): FilterConditionPluginImpl<VirtualClinicAnalysisFilterIges> {
    return new FilterConditionIgesPlugin(null);
  },
};

export class FilterConditionIgesPlugin extends FilterConditionPluginImpl<
  VirtualClinicAnalysisFilterIges,
  FilterConditionIgesDto
> {
  public static override Specification: FilterConditionPluginSpecification<
    VirtualClinicAnalysisFilterIges,
    FilterConditionIgesDto,
    BdIgesCondition
  > = FilterConditionIgesPluginType;

  constructor(private _iges: VirtualClinicAnalysisFilterIges) {
    super(FilterConditionIgesPluginType.type);
  }

  clone(): FilterConditionIgesPlugin {
    const iges = this._iges ? this._iges.clone() : null;

    return new FilterConditionIgesPlugin(iges);
  }

  override equals(other: FilterConditionIgesPlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this._iges, other._iges, (a, b) => a.equals(b))
    );
  }

  override toCondition(): BdCondition | null {
    return new BdIgesCondition(
        this._iges.einschlussKriterien,
        this._iges.k1DrgStationaer,
        this._iges.k2OpsStationaer,
        this._iges.k3StationaereBehandlungBeatmung,
        this._iges.k5IcdBetreuungsbedarf,
        this._iges.k6IcdStationaer,
        this._iges.k6sErhoehterAufwand,
        this._iges.k7Pccl,
        this._iges.k8Pflegegrade,
        this._iges.kSharpAlter,
      );
  }

  override validationToText(): string {
    return 'IGES Kriterien auswählen';
  }

  toJSON(): FilterConditionIgesDto | null {
    return {
      type: this.type,
      iges: this._iges,
    };
  }

  toText(): string | null {
    return 'IGES Kriterien';
  }

  toValue(): VirtualClinicAnalysisFilterIges {
    return this._iges;
  }
}

export class BdIgesCondition extends BdCondition {
  constructor(
    public einschlussKriterien: boolean | null,
    public k1DrgStationaer: boolean | null,
    public k2OpsStationaer: boolean | null,
    public k3StationaereBehandlungBeatmung: boolean | null,
    public k5IcdBetreuungsbedarf: boolean | null,
    public k6IcdStationaer: boolean | null,
    public k6sErhoehterAufwand: boolean | null,
    public k7Pccl: boolean | null,
    public k8Pflegegrade: boolean | null,
    public kSharpAlter: boolean | null,
  ) {
    super(BD_IGES_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        einschlussKriterien: this.einschlussKriterien,
        k1DrgStationaer: this.k1DrgStationaer,
        k2OpsStationaer: this.k2OpsStationaer,
        k3StationaereBehandlungBeatmung: this.k3StationaereBehandlungBeatmung,
        k6IcdStationaer: this.k6IcdStationaer,
        k5IcdBetreuungsbedarf: this.k5IcdBetreuungsbedarf,
        k8Pflegegrade: this.k8Pflegegrade,
        k6sErhoehterAufwand: this.k6sErhoehterAufwand,
        k7Pccl: this.k7Pccl,
        kSharpAlter: this.kSharpAlter,
      },
    };
  }
}

interface FilterConditionIgesDto {
  type: string;
  iges: object;
}
